/** D-161: the Academy activity reports e-mailed to the administrators — on/off per report, time,
 * attachment threshold, recipients (positions, members, addresses), preview, « send me now » and
 * the archive of the reports sent. */

import {
  type AcademySettings,
  useAcademyActivityReportsList,
  useAcademyActivityReportsPreviewCreate,
  useAcademyActivityReportsRetrieve,
  useAcademyActivityReportsSendMeCreate,
  useAcademySettingsPartialUpdate,
  useTeamMembersList,
} from "@agencyos/api-client";
import { Button, Checkbox, GlassCard, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { ErrorList, isObject, num, str, useAcademyRoles, useRefreshAcademy } from "./shared";

/** The positions offered as recipients (the owner and managers are chosen by default). */
export const REPORT_ROLES = ["owner", "manager", "academy_manager", "trainer", "advisor"] as const;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

export interface ReportSettings {
  daily: boolean;
  weekly: boolean;
  monthly: boolean;
  send_at: string;
  attachment_threshold: number;
  recipients: { roles: string[]; members: string[]; emails: string[] };
}

const strings = (value: unknown): string[] => (Array.isArray(value) ? value.map(str).filter(Boolean) : []);

/** The effective settings sent by the server (seed completed by the agency's values). */
export function reportSettingsOf(value: unknown): ReportSettings {
  const row = isObject(value) ? value : {};
  const recipients = isObject(row.recipients) ? row.recipients : {};
  return {
    daily: row.daily !== false,
    weekly: row.weekly !== false,
    monthly: row.monthly !== false,
    send_at: str(row.send_at) || "23:59",
    attachment_threshold: num(row.attachment_threshold) || 25,
    recipients: { roles: strings(recipients.roles), members: strings(recipients.members), emails: strings(recipients.emails) },
  };
}

/** One address per line (or separated by commas), trimmed, without duplicates. */
export function emailList(text: string): string[] {
  return [...new Set(text.split(/[\n,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
}

function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function Archive() {
  const { t } = useTranslation();
  const list = useAcademyActivityReportsList();
  const [open, setOpen] = useState<string | null>(null);
  const detail = useAcademyActivityReportsRetrieve(open ?? "", { query: { enabled: Boolean(open) } });
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  const shown = detail.data?.status === 200 ? detail.data.data : undefined;
  const download = (name: string, content: string) => {
    const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <section className="stack" aria-labelledby="activity-reports-archive">
      <h3 id="activity-reports-archive">{t("activityReports.archive")}</h3>
      {rows.length ? null : <p className="muted">{t("activityReports.noArchive")}</p>}
      <ul className="plain-list stack">
        {rows.map((row) => (
          <li key={row.id} className="row">
            <span>
              <strong>{row.day}</strong> · {t(`activityReports.kind.${row.kind ?? "scheduled"}`)} · {t(`activityReports.status.${row.status ?? "pending"}`)} ·{" "}
              {t("activityReports.recipientsCount", { count: Array.isArray(row.recipients) ? row.recipients.length : 0 })}
              {row.subject ? <span className="muted"> — {row.subject}</span> : null}
            </span>
            {row.status === "sent" ? (
              <Button variant="ghost" onClick={() => setOpen(open === row.id ? null : row.id)}>
                {open === row.id ? t("activityReports.close") : t("activityReports.view")}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {shown ? (
        <div className="stack">
          {shown.attachment ? (
            <div className="row">
              <Button variant="secondary" onClick={() => download(`academy-${shown.day}.csv`, shown.attachment ?? "")}>
                {t("activityReports.attachment")}
              </Button>
            </div>
          ) : null}
          <iframe className="doc-preview" title={shown.subject || t("activityReports.previewTitle")} sandbox="" srcDoc={shown.html} />
        </div>
      ) : null}
    </section>
  );
}

export function ActivityReportsSettings({ row }: { row: AcademySettings }) {
  const { t } = useTranslation();
  const { canEditSettings } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const save = useAcademySettingsPartialUpdate({ mutation: { onSuccess: refresh } });
  const members = useTeamMembersList({ query: { enabled: canEditSettings } });
  const preview = useAcademyActivityReportsPreviewCreate();
  const sendMe = useAcademyActivityReportsSendMeCreate({ mutation: { onSuccess: refresh } });
  const initial = reportSettingsOf(row.activity_reports_effective);
  const [form, setForm] = useState<ReportSettings>(initial);
  const [emails, setEmails] = useState(initial.recipients.emails.join("\n"));
  const [day, setDay] = useState(today);
  const errors = fieldErrors(save.error);
  const team = members.data?.status === 200 ? members.data.data.filter((m) => m.is_active) : [];
  const timeError = TIME.test(form.send_at) ? undefined : t("activityReports.sendAtError");
  const effective = isObject(row.activity_reports_effective) ? row.activity_reports_effective : {};
  const formula = str(effective.formula);
  const zone = str(effective.zone) || "Africa/Douala";
  const shown = preview.data?.status === 200 ? preview.data.data : undefined;

  const toggle = (list: "roles" | "members", value: string, on: boolean) =>
    setForm((f) => ({
      ...f,
      recipients: { ...f.recipients, [list]: on ? [...new Set([...f.recipients[list], value])] : f.recipients[list].filter((x) => x !== value) },
    }));

  const onSave = () => {
    if (timeError) return;
    save.mutate({
      id: row.id,
      data: {
        activity_reports: {
          daily: form.daily,
          weekly: form.weekly,
          monthly: form.monthly,
          send_at: form.send_at,
          attachment_threshold: form.attachment_threshold,
          recipients: { ...form.recipients, emails: emailList(emails) },
        },
      },
    });
  };

  return (
    <GlassCard as="section" className="stack" aria-labelledby="activity-reports-title">
      <h2 id="activity-reports-title">{t("activityReports.title")}</h2>
      <p className="muted">{t("activityReports.intro")}</p>
      <fieldset className="plain-fieldset stack" disabled={!canEditSettings}>
        <SoftToggle label={t("activityReports.daily")} checked={form.daily} disabled={!canEditSettings} onChange={(v) => setForm((f) => ({ ...f, daily: v }))} />
        <SoftToggle label={t("activityReports.weekly")} checked={form.weekly} disabled={!canEditSettings} onChange={(v) => setForm((f) => ({ ...f, weekly: v }))} />
        <SoftToggle label={t("activityReports.monthly")} checked={form.monthly} disabled={!canEditSettings} onChange={(v) => setForm((f) => ({ ...f, monthly: v }))} />
        <div className="form-grid">
          <TextField
            label={t("activityReports.sendAt")}
            hint={t("activityReports.sendAtHint", { zone })}
            type="time"
            value={form.send_at}
            error={timeError}
            onChange={(e) => setForm((f) => ({ ...f, send_at: e.target.value }))}
          />
          <TextField
            label={t("activityReports.threshold")}
            hint={t("activityReports.thresholdHint")}
            inputMode="numeric"
            value={String(form.attachment_threshold)}
            onChange={(e) => setForm((f) => ({ ...f, attachment_threshold: Number(e.target.value.replace(/\D/g, "")) || 1 }))}
          />
        </div>
        <h3>{t("activityReports.recipients")}</h3>
        <fieldset className="plain-fieldset row">
          <legend className="ao-footnote ao-secondary">{t("activityReports.roles")}</legend>
          {REPORT_ROLES.map((role) => (
            <Checkbox key={role} label={t(`roles.${role}`)} checked={form.recipients.roles.includes(role)} onChange={(e) => toggle("roles", role, e.target.checked)} />
          ))}
        </fieldset>
        <p className="muted">{t("activityReports.rolesHint")}</p>
        {canEditSettings ? (
          <fieldset className="plain-fieldset stack">
            <legend className="ao-footnote ao-secondary">{t("activityReports.members")}</legend>
            {team.length ? null : <p className="muted">{t("activityReports.noMembers")}</p>}
            {team.map((member) => (
              <Checkbox
                key={member.id}
                label={`${member.full_name || member.email} · ${t(`roles.${member.role ?? "advisor"}`)}`}
                checked={form.recipients.members.includes(member.id)}
                onChange={(e) => toggle("members", member.id, e.target.checked)}
              />
            ))}
          </fieldset>
        ) : null}
        <TextAreaField label={t("activityReports.emails")} hint={t("activityReports.emailsHint")} rows={3} value={emails} error={errors.activity_reports} onChange={(e) => setEmails(e.target.value)} />
        {save.error && !errors.activity_reports ? <ErrorList error={save.error} /> : null}
        <div className="row">
          <Button disabled={save.isPending || Boolean(timeError)} onClick={onSave}>
            {save.isPending ? t("common.loading") : t("common.save")}
          </Button>
          {save.isSuccess ? (
            <span role="status" className="ok">
              {t("common.saved")}
            </span>
          ) : null}
        </div>
      </fieldset>
      {formula ? (
        <p className="ao-footnote ao-secondary">
          <strong>{t("activityReports.formula")} : </strong>
          {formula}
        </p>
      ) : null}

      <h3>{t("activityReports.test")}</h3>
      <div className="row">
        <TextField label={t("activityReports.day")} type="date" value={day} max={today()} onChange={(e) => setDay(e.target.value)} />
        <Button variant="secondary" disabled={preview.isPending || !day} onClick={() => preview.mutate({ data: { day } })}>
          {preview.isPending ? t("common.loading") : t("activityReports.preview")}
        </Button>
        <Button variant="secondary" disabled={sendMe.isPending || !day} onClick={() => sendMe.mutate({ data: { day } })}>
          {t("activityReports.sendMe")}
        </Button>
      </div>
      {sendMe.isSuccess ? (
        <p role="status" className="ok">
          {t("activityReports.sent")}
        </p>
      ) : null}
      {preview.error ? <ErrorList error={preview.error} /> : null}
      {sendMe.error ? <ErrorList error={sendMe.error} /> : null}
      {shown ? <iframe className="doc-preview" title={t("activityReports.previewTitle")} sandbox="" srcDoc={shown.html} /> : null}
      <Archive />
    </GlassCard>
  );
}
